import { FlagDefinitionApi } from '@app/api';
import Editor, { BeforeMount, OnMount } from '@monaco-editor/react';
import { HelperText, HelperTextItem, Skeleton, Spinner } from '@patternfly/react-core';
import * as React from 'react';
import { RULE_LANGUAGE_ID, acquireRuleLanguageFeatures, setRuleDictionary } from './ruleMonaco';
import { useRuleDictionary } from './useRuleDictionary';

interface RuleEditorProps {
  value: string;
  onChange: (value: string) => void;
  onValidationChange: (isValid: boolean) => void;
  targetEntity: string;
}

export const RuleEditor: React.FC<RuleEditorProps> = ({
  value,
  onChange,
  onValidationChange,
  targetEntity
}) => {
  const [validationMsg, setValidationMsg] = React.useState<{
    text: string;
    status: 'success' | 'error' | 'default';
  }>({ text: '', status: 'default' });
  const [isValidating, setIsValidating] = React.useState(false);
  const [cursorPos, setCursorPos] = React.useState({ lineNumber: 1, column: 1 });

  const { dictionary, isLoading: isDictionaryLoading, error: dictionaryError } = useRuleDictionary();

  // The completion providers are registered against Monaco itself and read the dictionary from
  // module state, so a reload of it reaches an editor that is already open without touching the
  // registration. See ruleMonaco.ts for why that is not done per mount.
  React.useEffect(() => {
    setRuleDictionary(dictionary);
  }, [dictionary]);

  const releaseLanguageFeatures = React.useRef<(() => void) | null>(null);

  // Before the editor is created, not after: the model is built with the language id below, and a
  // model created before the language exists is left as plain text - no highlighting, and no
  // provider of ours attached to it.
  const handleEditorWillMount: BeforeMount = (monaco) => {
    // Releasing first keeps the count honest if the editor is ever rebuilt without this component
    // unmounting - one editor holds one claim, however many times it is created.
    releaseLanguageFeatures.current?.();
    releaseLanguageFeatures.current = acquireRuleLanguageFeatures(monaco);
  };

  const handleEditorDidMount: OnMount = (editor) => {
    editor.onDidChangeCursorPosition((e) => {
      setCursorPos({ lineNumber: e.position.lineNumber, column: e.position.column });
    });
  };

  React.useEffect(
    () => () => {
      releaseLanguageFeatures.current?.();
      releaseLanguageFeatures.current = null;
    },
    []
  );

  React.useEffect(() => {
    if (value) {
      setValidationMsg({ text: 'Waiting for typing to pause...', status: 'default' });
      onValidationChange(false);
    }

    const handler = setTimeout(async () => {
      if (!value) {
        setValidationMsg({ text: '', status: 'default' });
        onValidationChange(true);
        return;
      }

      setIsValidating(true);
      setValidationMsg({ text: 'Validating rule syntax...', status: 'default' });

      try {
        await FlagDefinitionApi.validateSyntax(value);

        setValidationMsg({ text: 'Syntax Valid', status: 'success' });
        onValidationChange(true);
      } catch (err) {
        console.error('Validation error:', err);
        const reported = err instanceof Error ? err.message : String(err);
        let errorMessage = 'Invalid JEXL syntax';

        try {
          const parsedError = JSON.parse(reported);
          if (parsedError.message) {
            errorMessage = parsedError.message.split('tokenization error').pop()?.trim() || parsedError.message;
          }
        } catch {
          errorMessage = reported || 'Invalid JEXL syntax';
        }

        setValidationMsg({ text: `Error: ${errorMessage}`, status: 'error' });
        onValidationChange(false);
      } finally {
        setIsValidating(false);
      }
    }, 2000);

    return () => clearTimeout(handler);
  }, [value, onValidationChange]);

  // The dictionary names its root after the entity a rule is written against - `klass` for KLASS.
  // A target the service exposes no data for is worth saying out loud: the rule can be written and
  // saved, and it will fail the first time it runs.
  const targetRoot = targetEntity.toLowerCase();
  const isTargetMissing =
    !isDictionaryLoading && !dictionaryError && targetRoot.length > 0 && !dictionary[targetRoot];

  return (
    <>
      <div style={{
        border: '1px solid var(--pf-v6-global--BorderColor--dark-100, #8a8d90)',
        borderBottom: '2px solid var(--pf-v6-global--BorderColor--dark-100, #8a8d90)',
        borderRadius: '4px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Editor
          height="200px"
          language={RULE_LANGUAGE_ID}
          theme="vs-light"
          value={value}
          onChange={(val) => onChange(val || '')}
          beforeMount={handleEditorWillMount}
          onMount={handleEditorDidMount}
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            lineNumbers: 'on',
            automaticLayout: true,
            scrollBeyondLastLine: false,
            tabSize: 4,
            // The suggestion list is wider and taller than a 200px editor: without this it is
            // clipped by the box the editor sits in.
            fixedOverflowWidgets: true,
            // Words already in the rule are not suggestions - they crowd out the dictionary with
            // whatever the author last typed, spelling mistakes included.
            wordBasedSuggestions: 'off',
            quickSuggestions: { other: true, comments: false, strings: false }
          }}
          loading={<Skeleton height="200px" />}
        />

        <div style={{
          backgroundColor: 'var(--pf-v6-global--BackgroundColor--200, #f0f0f0)',
          borderTop: '1px solid var(--pf-v6-global--BorderColor--100, #d2d2d2)',
          padding: '2px 12px',
          fontSize: '12px',
          display: 'flex',
          justifyContent: 'space-between',
          color: 'var(--pf-v6-global--Color--200, #6a6e73)',
          fontFamily: 'monospace'
        }}>
          {/* Neither the dot nor the colon is guessable, and Ctrl+Space is how you find out that
              anything is on offer at all. */}
          <span>. fields · : functions · Ctrl+Space anywhere</span>
          <span>Ln {cursorPos.lineNumber}, Col {cursorPos.column}</span>
        </div>
      </div>

      <HelperText>
        <HelperTextItem
          variant={validationMsg.status}
          icon={isValidating ? <Spinner size="md" /> : undefined}
        >
          {validationMsg.text}
        </HelperTextItem>

        {dictionaryError && (
          <HelperTextItem variant="warning">
            Autocomplete is unavailable — the field and function dictionary could not be read from
            the service ({dictionaryError.message}).
          </HelperTextItem>
        )}

        {isTargetMissing && (
          <HelperTextItem variant="warning">
            The service exposes no fields for {targetEntity}, so a rule written here has no record
            to read. Only rules targeting an entity in the dictionary can be evaluated.
          </HelperTextItem>
        )}
      </HelperText>
    </>
  );
};
